import type { Category } from '../../types';

interface CategoryFormData {
  name: string;
  icon: string;
  color: string;
  parentId: string | null;
}

interface CategoryFormProps {
  formData: CategoryFormData;
  onChange: (data: CategoryFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isEditing: boolean;
  rootCategories: Category[];
  editingCategoryId?: string;
  presetColors: string[];
  colorNames: Record<string, string>;
}

export function CategoryForm({
  formData,
  onChange,
  onSubmit,
  onCancel,
  isEditing,
  rootCategories,
  editingCategoryId,
  presetColors,
  colorNames,
}: CategoryFormProps) {
  return (
    <form
      onSubmit={onSubmit}
      className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4"
    >
      <h4 className="font-medium text-near-black">
        {isEditing ? 'Editar categoria' : 'Nueva categoria'}
      </h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="category-name"
            className="block text-sm text-mid-gray mb-1"
          >
            Nombre *
          </label>
          <input
            id="category-name"
            type="text"
            value={formData.name}
            onChange={(e) => onChange({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-[44px]"
            placeholder="Nombre de la categoria"
            autoComplete="off"
            required
          />
        </div>

        <div>
          <label
            htmlFor="category-icon"
            className="block text-sm text-mid-gray mb-1"
          >
            Emoji (opcional)
          </label>
          <input
            id="category-icon"
            type="text"
            value={formData.icon}
            onChange={(e) =>
              onChange({ ...formData, icon: e.target.value.slice(0, 2) })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-[44px]"
            placeholder="🏠"
            autoComplete="off"
            maxLength={2}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-mid-gray mb-2">Color</label>
        <div className="flex flex-wrap gap-2">
          {presetColors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => onChange({ ...formData, color })}
              className={`w-8 h-8 rounded-full border-2 transition ${
                formData.color === color
                  ? 'border-near-black scale-110'
                  : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: color }}
              aria-label={colorNames[color] ?? `Color ${color}`}
              aria-pressed={formData.color === color}
            />
          ))}
        </div>
      </div>

      <div>
        <label
          htmlFor="category-parent"
          className="block text-sm text-mid-gray mb-1"
        >
          Categoria padre (opcional)
        </label>
        <select
          id="category-parent"
          value={formData.parentId ?? ''}
          onChange={(e) =>
            onChange({ ...formData, parentId: e.target.value || null })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-[44px]"
        >
          <option value="">Sin padre (categoria principal)</option>
          {rootCategories
            .filter((c) => c.id !== editingCategoryId)
            .map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
        </select>
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-mid-gray hover:text-near-black transition min-h-[44px]"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-brand-secondary transition min-h-[44px]"
        >
          {isEditing ? 'Guardar cambios' : 'Crear categoria'}
        </button>
      </div>
    </form>
  );
}
